import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import omnicareLogo from "../../assets/icons/logo.svg";
import LanguageSwitcher from "../LanguageSwitcher";
import "../../styles/components/EnergyResults.css";

const computeScores = (data) => {
  // Circadian Score (0-100)
  let circadian = 50;
  if (data.morningLight === "daily") circadian += 25;
  else if (data.morningLight === "sometimes") circadian += 10;
  else if (data.morningLight === "rarely") circadian -= 10;
  else if (data.morningLight === "never") circadian -= 20;

  if (data.sleepConsistency === "very") circadian += 20;
  else if (data.sleepConsistency === "somewhat") circadian += 10;
  else if (data.sleepConsistency === "variable") circadian -= 10;
  else if (data.sleepConsistency === "random") circadian -= 20;

  if (data.caffeineTiming === "delayed") circadian += 15;
  else if (data.caffeineTiming === "hour") circadian -= 5;
  else if (data.caffeineTiming === "immediately") circadian -= 15;
  else if (data.caffeineTiming === "none") circadian += 5;

  if (data.eveningLight === "dim") circadian += 15;
  else if (data.eveningLight === "moderate") circadian -= 5;
  else if (data.eveningLight === "bright") circadian -= 15;
  else if (data.eveningLight === "very_bright") circadian -= 25;

  // Energy Score (0-100) 
  let energy = 50;
  if (data.afternoonEnergyDip === "stable") energy += 25;
  else if (data.afternoonEnergyDip === "mild_dip") energy += 10;
  else if (data.afternoonEnergyDip === "crash") energy -= 15;
  else if (data.afternoonEnergyDip === "sleepy") energy -= 25;

  if (data.exerciseFrequency === "optimal") energy += 20;
  else if (data.exerciseFrequency === "moderate") energy += 10;
  else if (data.exerciseFrequency === "minimal") energy -= 10;
  else if (data.exerciseFrequency === "none") energy -= 20;

  if (data.proteinTiming === "yes") energy += 15;
  else if (data.proteinTiming === "sometimes") energy += 5;
  else if (data.proteinTiming === "minimal") energy -= 10;

  if (data.hydrationElectrolytes === "optimal") energy += 15;
  else if (data.hydrationElectrolytes === "adequate") energy += 5;
  else if (data.hydrationElectrolytes === "poor") energy -= 15;

  // Recovery Score (0-100)
  let recovery = 50;
  if (data.nsdrPractice === "regular") recovery += 25;
  else if (data.nsdrPractice === "occasional") recovery += 10;
  else if (data.nsdrPractice === "tried") recovery += 5;

  if (data.coldHeatExposure === "both") recovery += 20;
  else if (data.coldHeatExposure === "cold" || data.coldHeatExposure === "heat") recovery += 10;

  if (data.stressLevels === "low_aware") recovery += 20;
  else if (data.stressLevels === "moderate_aware") recovery += 10;
  else if (data.stressLevels === "high_unaware") recovery -= 10;
  else if (data.stressLevels === "overwhelmed") recovery -= 20;

  if (data.recoveryPractices && data.recoveryPractices.length >= 3) recovery += 15;
  else if (data.recoveryPractices && data.recoveryPractices.length >= 2) recovery += 10;
  else if (data.recoveryPractices && data.recoveryPractices.length === 1) recovery += 5;

  if (data.fermentedFoods === "daily") recovery += 10;
  else if (data.fermentedFoods === "few_times") recovery += 5;

  circadian = Math.max(10, Math.min(100, circadian));
  energy = Math.max(10, Math.min(100, energy));
  recovery = Math.max(10, Math.min(100, recovery));

  return { circadian, energy, recovery };
};

const getSupplementRecs = (data) => {
  const recs = [];
  
  // Sleep & Circadian Support
  if (data.eveningLight === "bright" || data.eveningLight === "very_bright" || data.afternoonEnergyDip === "crash") {
    if (!data.supplementStack?.includes("Apigenin")) {
      recs.push({ 
        name: "Apigenin 50mg", 
        reason: "Promotes sleep onset and quality", 
        icon: "🌼",
        citation: "Huberman Lab Ep. 2: Sleep Toolkit"
      });
    }
    if (!data.supplementStack?.includes("Magnesium L-Threonate") && !data.supplementStack?.includes("Magnesium Bisglycinate")) {
      recs.push({ 
        name: "Magnesium L-Threonate", 
        reason: "Crosses blood-brain barrier for sleep + cognitive benefits", 
        icon: "🧠",
        citation: "Huberman Lab: sleep & learning protocols"
      });
    }
  }

  // Cognitive & Physical Performance
  if (data.exerciseFrequency === "optimal" || data.exerciseFrequency === "moderate") {
    if (!data.supplementStack?.includes("Creatine")) {
      recs.push({ 
        name: "Creatine Monohydrate 3-5g", 
        reason: "Muscle power + cognitive function (especially important for women)", 
        icon: "💪",
        citation: "Sims: ROAR (2016), Huberman: cognitive enhancement"
      });
    }
  }

  // Stress & Adaptation
  if (data.stressLevels === "high_unaware" || data.stressLevels === "overwhelmed") {
    if (!data.supplementStack?.includes("Ashwagandha")) {
      recs.push({ 
        name: "Ashwagandha 300-600mg", 
        reason: "Reduces cortisol, improves stress resilience (cycle off periodically)", 
        icon: "🌿",
        citation: "Huberman: stress management protocols"
      });
    }
    if (!data.supplementStack?.includes("Rhodiola Rosea")) {
      recs.push({ 
        name: "Rhodiola Rosea", 
        reason: "Adaptogen for stress response and mental fatigue", 
        icon: "🏔️",
        citation: "Research-backed adaptogen for HPA axis"
      });
    }
  }

  // Hydration & Electrolytes
  if (data.hydrationElectrolytes !== "optimal") {
    if (!data.supplementStack?.includes("LMNT/Electrolytes")) {
      recs.push({ 
        name: "Electrolyte Mix (Sodium/Potassium/Magnesium)", 
        reason: "Optimal hydration for cellular function and performance", 
        icon: "⚡",
        citation: "Huberman & Sims: hydration protocols"
      });
    }
  }

  // Gut Health & Microbiome
  if (data.fermentedFoods === "rarely" || data.fermentedFoods === "occasionally") {
    if (!data.supplementStack?.includes("AG1/Greens")) {
      recs.push({ 
        name: "Greens Powder (AG1-style)", 
        reason: "Micronutrient insurance when vegetable intake is low", 
        icon: "🥬",
        citation: "Micronutrient density for metabolic function"
      });
    }
  }

  // Inflammation & Mood
  if (!data.supplementStack?.includes("Omega-3 EPA")) {
    recs.push({ 
      name: "Omega-3 EPA (2g+)", 
      reason: "Anti-inflammatory, mood support, cardiovascular health", 
      icon: "🐟",
      citation: "Huberman: 2g+ EPA for mood benefits"
    });
  }

  // Foundation Nutrients
  if (!data.supplementStack?.includes("Vitamin D3 + K2")) {
    recs.push({ 
      name: "Vitamin D3 + K2", 
      reason: "Most people deficient — immune, bone, mood support", 
      icon: "☀️",
      citation: "K2 enhances D3 utilization and bone health"
    });
  }

  return recs.length > 0 ? recs.slice(0, 6) : [{ 
    name: "You're well-optimized!", 
    reason: "Your supplement stack looks comprehensive", 
    icon: "🎉",
    citation: "Keep monitoring and adjusting based on labs"
  }];
};

const detectRedFlags = (data) => {
  const flags = [];
  const age = parseInt(data.age, 10);
  if (age >= 40) flags.push("age40+");
  
  // Poor sleep + high stress + no exercise combo
  if (data.exerciseFrequency === "none" && 
      data.stressLevels === "overwhelmed" && 
      (data.afternoonEnergyDip === "crash" || data.afternoonEnergyDip === "sleepy")) {
    flags.push("triad");
  }
  
  // Severe circadian disruption
  if (data.morningLight === "never" && 
      data.eveningLight === "very_bright" && 
      data.sleepConsistency === "random") {
    flags.push("circadianChaos");
  }
  
  // Chronic stress without management
  if (data.stressLevels === "overwhelmed" && 
      data.nsdrPractice === "never" && 
      (!data.recoveryPractices || data.recoveryPractices.includes("None"))) {
    flags.push("chronicStress");
  }
  
  // Poor metabolic markers
  if (data.afternoonEnergyDip === "crash" && 
      data.proteinTiming === "minimal" && 
      data.hydrationElectrolytes === "poor") {
    flags.push("metabolicIssues");
  }
  
  return flags;
};

const getScoreColor = (score) => {
  if (score >= 75) return "#2d8a2d";
  if (score >= 50) return "#c68a00";
  return "#c44040";
};

const getScoreLabel = (score) => {
  if (score >= 80) return "Excellent";
  if (score >= 65) return "Good";
  if (score >= 45) return "Fair";
  return "Needs Attention";
};

const EnergyResultPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem("energy_quiz_data");
    if (stored) {
      setData(JSON.parse(stored));
    } else {
      navigate("/energy");
    }
  }, [navigate]);

  if (!data) return null;

  const scores = computeScores(data);
  const supplementRecs = getSupplementRecs(data);
  const redFlags = detectRedFlags(data);
  const showScreeningCTA = redFlags.length >= 2;

  const handleShareResults = () => {
    const text = `I just took the Pebble Health Optimization Quiz! Circadian: ${scores.circadian}/100, Energy: ${scores.energy}/100, Recovery: ${scores.recovery}/100. Get your personalized protocols: ${window.location.origin}/energy`;
    if (navigator.share) {
      navigator.share({ title: "My Wellness Optimization Results", text, url: `${window.location.origin}/energy` });
    } else {
      navigator.clipboard.writeText(text);
      alert("Results copied to clipboard!");
    }
  };

  const handleScreeningRedirect = () => {
    // Pre-fill shared fields for the cancer screening survey
    const prefill = {
      age: data.age,
      biologicalSex: data.biologicalSex,
      smokingHistory: data.smoking === "yes" ? "current" : data.smoking === "former" ? "former" : "never",
      alcoholConsumption: data.alcoholPerWeek,
    };
    localStorage.setItem("omnicare_prefill", JSON.stringify(prefill));
    navigate("/survey/1");
  };

  return (
    <div className="energy-results">
      {/* Header */}
      <header className="energy-header">
        <div className="logo" onClick={() => navigate("/landing")} style={{ cursor: "pointer" }}>
          <img src={omnicareLogo} alt="Pebble Health" className="logo-icon" />
          <span className="logo-text">Pebble Health</span>
        </div>
        <div className="header-actions">
          <LanguageSwitcher />
        </div>
      </header>

      <main className="energy-results-content">
        <h1 className="results-title">Your Wellness Profile</h1>
        <p className="results-subtitle">Here's what your answers tell us</p>

        {/* Score Cards */}
        <div className="score-cards">
          <div className="score-card">
            <div className="score-ring" style={{ borderColor: getScoreColor(scores.circadian) }}>
              <span className="score-number" style={{ color: getScoreColor(scores.circadian) }}>{scores.circadian}</span>
            </div>
            <h3>Circadian Score</h3>
            <span className="score-label" style={{ color: getScoreColor(scores.circadian) }}>{getScoreLabel(scores.circadian)}</span>
          </div>
          <div className="score-card">
            <div className="score-ring" style={{ borderColor: getScoreColor(scores.energy) }}>
              <span className="score-number" style={{ color: getScoreColor(scores.energy) }}>{scores.energy}</span>
            </div>
            <h3>Energy Score</h3>
            <span className="score-label" style={{ color: getScoreColor(scores.energy) }}>{getScoreLabel(scores.energy)}</span>
          </div>
          <div className="score-card">
            <div className="score-ring" style={{ borderColor: getScoreColor(scores.recovery) }}>
              <span className="score-number" style={{ color: getScoreColor(scores.recovery) }}>{scores.recovery}</span>
            </div>
            <h3>Recovery Score</h3>
            <span className="score-label" style={{ color: getScoreColor(scores.recovery) }}>{getScoreLabel(scores.recovery)}</span>
          </div>
        </div>

        {/* Supplement Recommendations */}
        <section className="results-section">
          <h2>💊 Protocol-Level Recommendations</h2>
          <div className="supplement-list">
            {supplementRecs.map((rec, i) => (
              <div key={i} className="supplement-card">
                <span className="supplement-icon">{rec.icon}</span>
                <div>
                  <strong>{rec.name}</strong>
                  <p>{rec.reason}</p>
                  {rec.citation && (
                    <small style={{ color: "#666", fontSize: "12px", fontStyle: "italic" }}>
                      📚 {rec.citation}
                    </small>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Red Flag / Screening CTA */}
        {showScreeningCTA && (
          <section className="screening-callout">
            <div className="screening-callout-inner">
              <span className="screening-icon">🩺</span>
              <div>
                <h3>You may benefit from a deeper health screening</h3>
                <p>
                  Based on your profile, we recommend a quick cancer risk assessment.
                  It's free, takes 3 minutes, and could be life-saving.
                </p>
                <button className="screening-cta-button" onClick={handleScreeningRedirect}>
                  Take the Health Screening →
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Share */}
        <div className="results-actions">
          <button className="share-button" onClick={handleShareResults}>
            📤 Share My Results
          </button>
          <button className="retake-button" onClick={() => navigate("/energy")}>
            🔄 Retake Quiz
          </button>
        </div>
      </main>
    </div>
  );
};

export default EnergyResultPage;
